import { useEffect, useRef } from "react";
import { Link } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import BuyTicketsButton from "@/components/BuyTicketsButton";
import { hasTicketsToView, myTicketsTabHref, shouldShowBuyTickets, VIEW_MY_TICKETS_LABEL } from "@/lib/eventCta";
import { useMyTicketsForEvent } from "@/hooks/useMyTicketsForEvent";
import { useServerNow } from "@/lib/serverClock";
import { isPastCalendarEvent } from "@/lib/calendarFilters";
import { useEventClock } from "@/hooks/useEventClock";
import { useHomepagePromotionPricing } from "@/hooks/useHomepagePromotionPricing";
import FeaturedEventPrice from "@/components/FeaturedEventPrice";
import type { CHIEvent } from "@/lib/eventsData";

/**
 * Lunar New Year campaign art and layout. The current event record owns all
 * visible title, date, time, location, pricing, and action details.
 *
 * One static event panel with a rotating decorative backdrop, not a content
 * carousel. Event facts are sourced from the selected record.
 *
 * It is built to live inside `.home-hero-stage`, so the Premier Partners band
 * still overlays its lower edge on wide screens exactly as it does for the
 * carousel: full viewport height, with the band's height padded off the bottom
 * so the lockup centres in the space above it rather than behind it.
 */

/** 元寶 — the gold ingot that falls through the hero. */
const GOLD_INGOT = "/media/lny2027/gold-ingot.svg";

/** The event this campaign sells. Its page owns registration and pricing. */
/**
 * The backdrop, as stills until the sizzle reel is cut.
 *
 * The original gala-ballroom image stays first and as the motion-safe fallback.
 * New campaign artwork is decorative media, not a claim about the event venue.
 *
 * Each plate is held, slowly pushed in and panned, then crossed into the next.
 * Adding another is one line: drop the file in `client/public/media/lny2027/`
 * and give it an origin to drift toward. When the reel exists, pass it as
 * `backgroundVideoSrc` and these step aside — the scrim and every layer above
 * it are already built to sit over moving footage.
 */
const BACKGROUND_PLATES = [
  { src: "/media/lny2027/gala-ballroom.webp", origin: "46% 42%", panX: "-1.2%", panY: "0.5%" },
  { src: "/media/lny2027/lantern-canopy.webp", origin: "50% 38%", panX: "1.1%", panY: "0.4%" },
  { src: "/media/lny2027/golden-blossoms.webp", origin: "50% 46%", panX: "-0.7%", panY: "-0.9%" },
  { src: "/media/lny2027/gala-tables.webp", origin: "50% 58%", panX: "0.8%", panY: "-0.6%" },
  { src: "/media/lny2027/lantern-garden.webp", origin: "50% 44%", panX: "-0.9%", panY: "0.7%" },
] as const;

/** Each still holds for ten seconds; two seconds of overlap keeps every transition gapless. */
const PLATE_INTERVAL_SECONDS = 10;
const PLATE_CROSSFADE_SECONDS = 2;
const PLATE_FADE_IN_SECONDS = 1;
const PLATE_FADE_OUT_SECONDS = PLATE_CROSSFADE_SECONDS;
const PLATE_ACTIVE_SECONDS = PLATE_INTERVAL_SECONDS + PLATE_CROSSFADE_SECONDS;
const PLATE_HOLD_SECONDS = PLATE_ACTIVE_SECONDS - PLATE_FADE_IN_SECONDS - PLATE_FADE_OUT_SECONDS;
/** Derive the cycle from the actual plate count so adding a still cannot desync the animation. */
const PLATE_CYCLE_SECONDS = PLATE_INTERVAL_SECONDS * BACKGROUND_PLATES.length;
const PLATE_FADE_IN_END_PERCENT = (PLATE_FADE_IN_SECONDS / PLATE_CYCLE_SECONDS) * 100;
const PLATE_HOLD_END_PERCENT = ((PLATE_FADE_IN_SECONDS + PLATE_HOLD_SECONDS) / PLATE_CYCLE_SECONDS) * 100;
const PLATE_ACTIVE_END_PERCENT = (PLATE_ACTIVE_SECONDS / PLATE_CYCLE_SECONDS) * 100;

/**
 * The curtain holds, then draws back, and the first plate starts its push as it
 * clears. One number so the two cannot drift: the plates wait exactly as long as
 * the curtain takes to get out of the way.
 */
const CURTAIN_HOLD_SECONDS = 0.5;
const CURTAIN_DRAW_SECONDS = 2.1;
const REVEAL_SECONDS = CURTAIN_HOLD_SECONDS + CURTAIN_DRAW_SECONDS;

export default function LunarNewYearHero({
  backgroundVideoSrc,
  event,
}: {
  /** The sizzle reel, once it is cut. Replaces the still loop outright. */
  backgroundVideoSrc?: string;
  event?: CHIEvent;
} = {}) {
  const fxRef = useRef<HTMLDivElement>(null);
  // CHI-22: the same data path and Buy Tickets rule as every other event CTA; nothing here is event-specific.
  const { isAuthenticated } = useAuth();
  const eventClock = useEventClock();
  const eventSlug = event?.slug ?? "lunar-new-year-celebration-2027";
  const title = event?.title ?? "Lunar New Year";
  const celebration = title.match(/\s+(Celebration(?:\s+\d{4})?)$/i)?.[1];
  const pricing = useHomepagePromotionPricing(eventSlug);
  // Signed out: no ticket call. One event, so Dev3's per-event hook (Dev2's hasBlockingTicket).
  const heroTickets = useMyTicketsForEvent(eventSlug, isAuthenticated && Boolean(event));
  const ctaClock = new Date(useServerNow(pricing.quote?.serverNow ?? heroTickets.serverNow, 30_000));
  const heroHasEnded = Boolean(event && (isPastCalendarEvent(event, eventClock) || isPastCalendarEvent(event, ctaClock)));
  // CHI-20: CTA priority is paid buyer (View My Ticket(s)) > Buy Tickets > Reserve Your Spot; an ended event shows none.
  const showViewMyTickets = isAuthenticated && !heroHasEnded
    && hasTicketsToView({ activeTicketCount: heroTickets.activeTickets.length, finalizing: heroTickets.status === "ok" && heroTickets.finalizing });
  const showBuyTickets = shouldShowBuyTickets(
    {
      slug: eventSlug,
      pricingMode: pricing.policy?.pricingMode,
      isSpecialEvent: event?.isSpecialEvent === true,
      hasEnded: heroHasEnded,
      ticketsCheckoutEnabled: pricing.policy?.ticketsCheckoutEnabled === true,
    },
    {
      hasBlockingTicket: heroTickets.hasBlockingTicket,
    },
  );
  const showAvailableBuyTickets = showBuyTickets && pricing.quoteUsable && pricing.quote?.available === true;
  const unavailable = pricing.policyError || pricing.quoteFailed || pricing.quote?.available === false || (pricing.quote?.seatsLeft != null && pricing.quote.seatsLeft <= 0);

  /*
   * Falling gold ingots.
   *
   * The lotus does not fall — it is the mark, and a mark scattered like weather
   * reads as confetti. It sits behind the lockup at size instead, and the thing
   * tumbling through the hero is 元寶, the ingot that means prosperity at the new
   * year. Each gets its own size, drift, tumble and shimmer phase from custom
   * properties, so the whole set shares two keyframe rules.
   *
   * Skipped outright when the visitor asks for reduced motion: there is no
   * meaning here to preserve, only decoration.
   */
  useEffect(() => {
    const fx = fxRef.current;
    if (!fx) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const count = window.innerWidth < 720 ? 5 : 10;
    const random = Math.random;

    for (let index = 0; index < count; index += 1) {
      const ingot = document.createElement("i");
      ingot.className = "lny-hero__ingot";

      const image = document.createElement("img");
      image.src = GOLD_INGOT;
      image.alt = "";
      image.setAttribute("aria-hidden", "true");
      ingot.appendChild(image);

      ingot.style.cssText = [
        `--x:${(3 + random() * 92).toFixed(1)}%`,
        `--s:${Math.round(32 + random() * 32)}px`,
        `--o:${(0.5 + random() * 0.4).toFixed(2)}`,
        `--dx:${Math.round((random() - 0.55) * 200)}px`,
        // A lazy half-turn or so, not a spin: a heavy thing tumbles slowly.
        `--rot:${Math.round(100 + random() * 200)}deg`,
        `--tilt:${Math.round(random() * 40 - 20)}deg`,
        `--fd:${(17 + random() * 15).toFixed(1)}s`,
        `--sd:${(2.8 + random() * 2.4).toFixed(1)}s`,
        `--dl:-${(random() * 30).toFixed(1)}s`,
        `--sdl:-${(random() * 4).toFixed(1)}s`,
      ].join(";");

      fx.appendChild(ingot);
    }

    return () => {
      fx.replaceChildren();
    };
  }, []);

  return (
    <section className="lny-hero" aria-label={`Featured event: ${event?.title ?? "Lunar New Year"}`}>
      <div className="lny-hero__bg" aria-hidden="true" />

      {/* The moving backdrop, behind every focal element and under its own scrim. */}
      <div className="lny-hero__media" aria-hidden="true">
        {backgroundVideoSrc ? (
          <video
            className="lny-hero__video"
            src={backgroundVideoSrc}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />
        ) : (
          BACKGROUND_PLATES.map((plate, index) => (
            <div
              key={plate.src}
              data-testid="lny-backdrop-plate"
              data-plate-index={index}
              className={`lny-hero__plate${index === 0 ? " lny-hero__plate--primary" : ""}`}
              style={{
                backgroundImage: `url(${plate.src})`,
                transformOrigin: plate.origin,
                animationDuration: `${PLATE_CYCLE_SECONDS}s`,
                // Begin the first fade one second before the curtain clears so
                // the original image is already present when the room opens.
                animationDelay: `${REVEAL_SECONDS - PLATE_FADE_IN_SECONDS + PLATE_INTERVAL_SECONDS * index}s`,
                "--lny-plate-pan-x": plate.panX,
                "--lny-plate-pan-y": plate.panY,
              } as React.CSSProperties}
            />
          ))
        )}

        {/* Drawn back once on arrival, never again. */}
        <div className="lny-hero__curtain lny-hero__curtain--left" />
        <div className="lny-hero__curtain lny-hero__curtain--right" />
      </div>
      <div className="lny-hero__scrim" aria-hidden="true" />
      <div className="lny-hero__glow lny-hero__glow--one" aria-hidden="true" />
      <div className="lny-hero__glow lny-hero__glow--two" aria-hidden="true" />
      <div className="lny-hero__vignette" aria-hidden="true" />
      <div className="lny-hero__fx" ref={fxRef} aria-hidden="true" />

      <div className="lny-hero__blossoms lny-hero__blossoms--tl" aria-hidden="true">
        <img src="/media/lny2027/blossoms-tl.webp" alt="" />
      </div>
      <div className="lny-hero__blossoms lny-hero__blossoms--br" aria-hidden="true">
        <img src="/media/lny2027/blossoms-br.webp" alt="" />
      </div>

      <div className="lny-hero__center">
        <span className="lny-hero__medallion">
          <img src="/media/lny2027/fire-goat-emblem.webp" alt="" aria-hidden="true" />
        </span>
        <p className="lny-hero__presents">The CHI Alliance Presents</p>
        <h1 className="lny-hero__title">{celebration ? title.slice(0, -celebration.length).trim() : title}</h1>
        <p className="lny-hero__celebration">{event ? event.subtitle || celebration || event.category : "Celebration 2027"}</p>

        <div className="lny-hero__cards">
          <FactCard
            eyebrow="Date"
            lead={event ? event.displayDate || event.date : "Saturday"}
            sub={event ? event.time || "Time to be announced" : "February 20, 2027"}
            photo="/media/lny2027/gala-ballroom.webp"
            photoPosition="50% 16%"
            icon={
              <svg className="lny-hero__ico" viewBox="0 0 48 48" aria-hidden="true">
                <rect x="8" y="11" width="32" height="29" rx="3" />
                <path d="M8 19h32M16 7v8M32 7v8" />
              </svg>
            }
          />

          <FactCard
            eyebrow="Location"
            lead={event ? event.location || "Location to be announced" : "The Ace Event Venue"}
            sub={event ? event.type === "virtual" ? "Virtual event" : "In person" : <>3050 N Josey Ln #160<br />Carrollton, TX 75007</>}
            photo="/media/lny2027/gala-ballroom.webp"
            photoPosition="20% 64%"
            icon={
              <svg className="lny-hero__ico" viewBox="0 0 48 48" aria-hidden="true">
                <path d="M24 42s-13-12.2-13-21A13 13 0 0 1 24 8a13 13 0 0 1 13 13c0 8.8-13 21-13 21z" />
                <circle cx="24" cy="21" r="4.4" />
              </svg>
            }
          />

          <FactCard
            eyebrow={event ? "Featuring" : "Audience"}
            lead={event ? event.speaker || event.category : "Professionals • Founders"}
            sub={event ? event.speakerOrg || event.category : "Executives • Business Owners"}
            photo="/media/lny2027/gala-ballroom.webp"
            photoPosition="80% 80%"
            icon={
              <svg className="lny-hero__ico" viewBox="0 0 48 48" aria-hidden="true">
                <circle cx="17" cy="16" r="4.2" />
                <circle cx="31" cy="17.2" r="3.6" />
                <circle cx="24" cy="15.2" r="3.2" />
                <path d="M8.5 34.5c.7-6 4.4-9.2 8.5-9.2s7.8 3.2 8.5 9.2M25 33.8c.5-4.2 3-6.6 5.8-6.6s5.4 2.4 6 6.6" />
              </svg>
            }
          />
        </div>

        <div className="lny-hero__cta">
          {/*
            The event's own page, routed in-app rather than linked absolutely:
            an https://thechialliance.com/... href would send a visitor out to
            the network and back for a page this bundle already has, and would
            point production traffic at production from every other environment.
          */}
          {!event || heroHasEnded ? null : showViewMyTickets ? (
            <Link href={myTicketsTabHref(event.slug)} className="lny-hero__btn lny-hero__btn--gold" data-testid="view-my-tickets">
              {VIEW_MY_TICKETS_LABEL} <span aria-hidden="true">→</span>
            </Link>
          ) : heroTickets.status === "ok" && heroTickets.finalizing ? (
            <span role="status">Preparing your tickets…</span>
          ) : showAvailableBuyTickets ? (
            <BuyTicketsButton slug={event.slug} className="lny-hero__btn lny-hero__btn--gold" />
          ) : unavailable ? (
            <Link href={`/events/${event.slug}`} className="lny-hero__btn lny-hero__btn--gold">
              Event details <span aria-hidden="true">→</span>
            </Link>
          ) : (
            <Link href={`/events/${event.slug}`} className="lny-hero__btn lny-hero__btn--gold">
              Reserve Your Spot <span aria-hidden="true">→</span>
            </Link>
          )}
          <Link href="/collaborate-with-us" className="lny-hero__btn lny-hero__btn--sponsor">
            Become a Sponsor
          </Link>
        </div>

        {event && <FeaturedEventPrice pricing={pricing} className="homepage-event-price--lny" />}
      </div>

      <style>{`
        .lny-hero{
          --lny-gold:#E8C547;--lny-gold-bright:#F6E27A;--lny-ivory:#FFF6E8;
          --lny-serif:'Cormorant Garamond',Georgia,'Times New Roman',serif;
          position:relative;z-index:1;display:flex;flex-direction:column;justify-content:center;
          height:calc(100svh - var(--chi-partners-band));min-height:0;overflow:hidden;isolation:isolate;
          padding:clamp(6.5rem,10vw,8.5rem) 0 clamp(2.4rem,4vw,3.4rem);
          background:linear-gradient(160deg,#8E0F1C 0%,#6E0A15 45%,#3E050C 100%);
          border-bottom:1px solid rgba(255,255,255,.08);
          color:var(--lny-ivory);
        }
        /* Inside the shared stage the band overlays the hero's foot, so the hero
           takes the whole viewport and pads the band's height off the bottom —
           the same arrangement the carousel uses. */
        @media (min-width:841px){
          .home-hero-stage .lny-hero{height:auto;min-height:100svh;padding-bottom:calc(var(--chi-partners-band) + clamp(1.6rem,2.5vw,2.4rem))}
        }

        /* The moving backdrop. Stills cross-fade on a Ken Burns push until the reel
           exists; a <video> drops into the same box and inherits the same scrim,
           so nothing above this layer has to change when it does. */
        .lny-hero__media{position:absolute;inset:0;z-index:0;overflow:hidden;pointer-events:none}
        .lny-hero__video{width:100%;height:100%;object-fit:cover;display:block}
        .lny-hero__plate{position:absolute;inset:0;opacity:0;background-size:cover;background-position:center;
          animation-name:lnyPlateCycle;animation-duration:${PLATE_CYCLE_SECONDS}s;animation-timing-function:ease-in-out;
          animation-iteration-count:infinite;animation-delay:var(--lny-plate-delay,0s);animation-fill-mode:both;will-change:transform,opacity}

        /* The house curtain. Two panels over the backdrop, held shut for a beat
           and then drawn apart — the room opening rather than a page loading.
           It runs once and stays open: forwards is what keeps it from snapping
           shut when the animation ends. Folds are a repeating gradient rather
           than an image, so the velvet costs nothing to ship. */
        .lny-hero__curtain{position:absolute;top:0;bottom:0;width:52%;z-index:2;pointer-events:none;
          background:
            repeating-linear-gradient(90deg,rgba(0,0,0,.34) 0 3px,rgba(255,255,255,.06) 16px,rgba(0,0,0,.26) 38px),
            linear-gradient(90deg,#5C0712 0%,#9B0B1C 42%,#6B0814 100%);
          animation-duration:2.1s;animation-delay:.5s;animation-timing-function:cubic-bezier(.3,.72,.3,1);
          animation-fill-mode:forwards;will-change:transform}
        .lny-hero__curtain--left{left:0;transform-origin:left center;animation-name:lnyCurtainLeft;
          box-shadow:inset -3px 0 0 rgba(212,175,55,.85),inset -26px 0 46px rgba(0,0,0,.5)}
        .lny-hero__curtain--right{right:0;transform-origin:right center;animation-name:lnyCurtainRight;
          box-shadow:inset 3px 0 0 rgba(212,175,55,.85),inset 26px 0 46px rgba(0,0,0,.5)}
        @keyframes lnyCurtainLeft{
          0%{transform:translate3d(0,0,0) scaleX(1)}
          100%{transform:translate3d(-101%,0,0) scaleX(.82)}}
        @keyframes lnyCurtainRight{
          0%{transform:translate3d(0,0,0) scaleX(1)}
          100%{transform:translate3d(101%,0,0) scaleX(.82)}}
        @keyframes lnyPlateCycle{
          0%{opacity:0;transform:scale(1.045) translate3d(0,0,0)}
          ${PLATE_FADE_IN_END_PERCENT}%{opacity:1;transform:scale(1.075) translate3d(0,0,0)}
          ${PLATE_HOLD_END_PERCENT}%{opacity:1;transform:scale(1.12) translate3d(var(--lny-plate-pan-x,0),var(--lny-plate-pan-y,0),0)}
          ${PLATE_ACTIVE_END_PERCENT}%{opacity:0;transform:scale(1.14) translate3d(var(--lny-plate-pan-x,0),var(--lny-plate-pan-y,0),0)}
          100%{opacity:0;transform:scale(1.14) translate3d(var(--lny-plate-pan-x,0),var(--lny-plate-pan-y,0),0)}}

        /* What keeps the room readable: the backdrop is a suggestion of the night,
           not the subject. Crimson is held on top of it so the hero stays the
           brand's colour rather than the photograph's. */
        .lny-hero__scrim{position:absolute;inset:0;z-index:0;pointer-events:none;
          background:
            linear-gradient(180deg,rgba(60,6,14,.82) 0%,rgba(74,8,18,.72) 42%,rgba(40,4,10,.88) 100%),
            radial-gradient(70% 55% at 50% 42%,rgba(120,12,26,.42),rgba(40,4,10,.86) 100%);
          backdrop-filter:saturate(1.08);-webkit-backdrop-filter:saturate(1.08)}

        /* A card turns on hover, never on a timer.

           The seam: two stacked faces with their own borders and radii leave a
           hairline where they meet mid-turn, and the backdrop shows through the
           rounded corners. Clipping on the inner, with the radius and the
           background living there instead of on each face, means there is one
           edge in the card rather than two fighting to line up. */
        .lny-hero__card{position:relative;min-height:16.5rem;perspective:1200px;background:none;border:0;box-shadow:none;padding:0;outline:none}
        .lny-hero__card-inner{position:relative;width:100%;height:100%;min-height:16.5rem;border-radius:10px;
          transform-style:preserve-3d;transition:transform .72s cubic-bezier(.23,1,.32,1);will-change:transform}
        .lny-hero__card:hover .lny-hero__card-inner,
        .lny-hero__card:focus-visible .lny-hero__card-inner{transform:rotateY(180deg)}
        .lny-hero__card:focus-visible .lny-hero__card-inner{box-shadow:0 0 0 3px rgba(246,226,122,.65)}
        .lny-hero__face{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;
          -webkit-backface-visibility:hidden;backface-visibility:hidden;overflow:hidden;
          padding:2.1rem 1.4rem 1.9rem;text-align:center;
          border:1.5px solid var(--lny-gold);border-radius:10px;background-clip:padding-box;
          box-shadow:0 10px 28px rgba(0,0,0,.22),inset 0 1px 0 rgba(255,241,184,.18),inset 0 0 30px rgba(232,197,71,.06)}
        .lny-hero__face--front{transform:rotateY(0deg) translateZ(1px);
          background:linear-gradient(160deg,rgba(160,24,40,.55),rgba(90,8,18,.55))}
        /* cover, centred, and pushed under a gradient — no letterbox edge, and the
           same words stay as readable here as they are on the front. */
        .lny-hero__face--back{transform:rotateY(180deg) translateZ(1px);
          background-size:cover;background-repeat:no-repeat;background-color:#3B0610}
        .lny-hero__face--back::after{content:"";position:absolute;inset:0;border-radius:inherit;
          background:linear-gradient(165deg,rgba(74,8,18,.84) 0%,rgba(30,3,9,.68) 45%,rgba(20,2,6,.9) 100%)}
        .lny-hero__face-content{position:relative;z-index:1;display:flex;flex-direction:column;align-items:center}
        .lny-hero__face-content .lny-hero__lead,
        .lny-hero__face-content .lny-hero__sub,
        .lny-hero__face-content .lny-hero__eyebrow{text-shadow:0 2px 12px rgba(0,0,0,.8)}

        .lny-hero__bg{position:absolute;inset:0;z-index:0;pointer-events:none;background:
          radial-gradient(ellipse at 50% 30%,rgba(180,20,40,.2),transparent 50%),
          linear-gradient(180deg,rgba(142,16,28,.68) 0%,rgba(107,8,20,.6) 48%,rgba(74,6,16,.8) 100%),
          url("/media/lny2027/gala-ballroom.webp") center/cover no-repeat}
        .lny-hero__glow{position:absolute;inset:-20%;z-index:0;pointer-events:none;will-change:transform,opacity}
        .lny-hero__glow--one{background:radial-gradient(40% 35% at 50% 38%,rgba(246,226,122,.22),transparent 70%),radial-gradient(30% 30% at 18% 80%,rgba(196,26,50,.45),transparent 70%);animation:lnyGlowDrift 26s ease-in-out infinite alternate}
        .lny-hero__glow--two{background:radial-gradient(35% 30% at 82% 22%,rgba(232,197,71,.16),transparent 70%),radial-gradient(45% 40% at 70% 90%,rgba(40,2,8,.55),transparent 70%);animation:lnyGlowDrift2 19s ease-in-out infinite alternate}
        @keyframes lnyGlowDrift{0%{transform:translate3d(-3%,-2%,0) scale(1);opacity:.75}50%{opacity:1}100%{transform:translate3d(3%,2%,0) scale(1.08);opacity:.85}}
        @keyframes lnyGlowDrift2{0%{transform:translate3d(2%,1%,0) scale(1.05);opacity:.6}100%{transform:translate3d(-2%,-3%,0) scale(1);opacity:1}}

        /* No damask lattice here any more. A repeating stamp tiled over moving
           footage reads as a screen laid on the picture, and the whole point of
           the backdrop is that it is photography. */
        .lny-hero__vignette{position:absolute;inset:0;z-index:1;pointer-events:none;background:radial-gradient(120% 90% at 50% 45%,transparent 55%,rgba(25,0,4,.55) 100%)}

        .lny-hero__blossoms{position:absolute;z-index:2;pointer-events:none}
        .lny-hero__blossoms img{width:100%;height:100%;object-fit:cover;display:block;filter:saturate(1.22) contrast(1.06)}
        .lny-hero__blossoms--tl{top:0;left:0;width:36%;height:52%;
          -webkit-mask-image:linear-gradient(to right,#000 70%,transparent 100%),linear-gradient(to bottom,#000 72%,transparent 100%);-webkit-mask-composite:source-in;
          mask-image:linear-gradient(to right,#000 70%,transparent 100%),linear-gradient(to bottom,#000 72%,transparent 100%);mask-composite:intersect}
        .lny-hero__blossoms--tl img{object-position:left top;transform-origin:0 0;animation:lnySwayTL 11s ease-in-out infinite}
        .lny-hero__blossoms--br{bottom:0;right:0;width:31%;height:48%;
          -webkit-mask-image:linear-gradient(to left,#000 70%,transparent 100%),linear-gradient(to top,#000 70%,transparent 100%);-webkit-mask-composite:source-in;
          mask-image:linear-gradient(to left,#000 70%,transparent 100%),linear-gradient(to top,#000 70%,transparent 100%);mask-composite:intersect}
        .lny-hero__blossoms--br img{object-position:right bottom;transform-origin:100% 100%;animation:lnySwayBR 13s ease-in-out infinite}
        @keyframes lnySwayTL{0%,100%{transform:rotate(0)}50%{transform:rotate(1.4deg)}}
        @keyframes lnySwayBR{0%,100%{transform:rotate(0)}50%{transform:rotate(-1.2deg)}}

        /* Falling 元寶, shimmering as they tumble.

           Above the lockup, not behind it. Gold dropping past the cards reads as
           falling through the room; the same gold behind them reads as wallpaper.
           Ignoring pointer events is what makes that safe: the layer covers the
           buttons and the cards, and every click and hover passes straight
           through it to them. */
        .lny-hero__fx{position:absolute;inset:0;z-index:4;pointer-events:none;overflow:hidden}
        .lny-hero__ingot{position:absolute;top:-90px;left:var(--x,50%);width:var(--s,40px);opacity:0;
          animation-name:lnyIngotFall,lnyIngotShimmer;animation-timing-function:linear,ease-in-out;
          animation-iteration-count:infinite,infinite;animation-duration:var(--fd,22s),var(--sd,4s);
          animation-delay:var(--dl,0s),var(--sdl,0s);will-change:transform,opacity,filter}
        .lny-hero__ingot img{width:100%;height:auto;display:block}
        @keyframes lnyIngotFall{
          0%{transform:translate3d(0,0,0) rotate(var(--tilt,0deg)) scale(.95);opacity:0}
          12%{opacity:var(--o,.75)}84%{opacity:var(--o,.75)}
          100%{transform:translate3d(var(--dx,-80px),118vh,0) rotate(calc(var(--tilt,0deg) + var(--rot,200deg))) scale(1.03);opacity:0}}
        @keyframes lnyIngotShimmer{
          0%,100%{filter:drop-shadow(0 2px 6px rgba(80,40,0,.45)) brightness(.97) saturate(1)}
          50%{filter:drop-shadow(0 0 18px rgba(255,228,140,.9)) brightness(1.3) saturate(1.15)}}

        .lny-hero__center{position:relative;z-index:3;width:min(920px,calc(100% - 2.2rem));margin:0 auto;text-align:center}
        .lny-hero__medallion{position:relative;display:block;width:min(200px,42vw);margin:0 auto .7rem}
        .lny-hero__medallion img{width:100%;height:auto;display:block;filter:drop-shadow(0 10px 28px rgba(0,0,0,.35))}
        .lny-hero__medallion::before{content:"";position:absolute;inset:-18%;border-radius:50%;background:radial-gradient(circle,rgba(246,226,122,.35),transparent 65%);z-index:-1;animation:lnyHalo 6s ease-in-out infinite}
        @keyframes lnyHalo{0%,100%{opacity:.55;transform:scale(.96)}50%{opacity:1;transform:scale(1.04)}}

        .lny-hero__presents{margin:0 0 .35rem;color:var(--lny-gold-bright);font-family:'Outfit',system-ui,sans-serif;font-size:.72rem;font-weight:600;letter-spacing:.32em;text-transform:uppercase}
        .lny-hero__title{margin:0;font-family:var(--lny-serif);font-weight:700;font-size:clamp(2.8rem,7.6vw,5.8rem);line-height:.9;letter-spacing:.06em;text-transform:uppercase;
          background-image:linear-gradient(110deg,transparent 42%,rgba(255,255,240,.9) 50%,transparent 58%),linear-gradient(180deg,#FFF8D2 0%,#F6E27A 32%,#E8C547 62%,#C9A227 100%);
          background-size:250% 100%,100% 100%;background-repeat:no-repeat;-webkit-background-clip:text;background-clip:text;color:transparent;-webkit-text-fill-color:transparent;
          filter:drop-shadow(0 10px 22px rgba(0,0,0,.48));animation:lnySheen 7s ease-in-out infinite;animation-delay:.6s}
        @keyframes lnySheen{0%,55%{background-position:120% 0,0 0}100%{background-position:-30% 0,0 0}}
        .lny-hero__celebration{display:flex;align-items:center;justify-content:center;gap:1rem;margin:.7rem 0 .45rem;font-family:var(--lny-serif);font-size:clamp(1.05rem,2vw,1.45rem);letter-spacing:.46em;text-transform:uppercase;color:#FFF1B8}
        .lny-hero__celebration:before,.lny-hero__celebration:after{content:"";flex:1;max-width:4.8rem;height:1px;background:var(--lny-gold)}
        .lny-hero__year{margin:0;color:var(--lny-gold);font-family:'Outfit',system-ui,sans-serif;font-size:.82rem;letter-spacing:.28em;text-transform:uppercase}

        .lny-hero__cards{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:1.25rem;margin:2rem 0 0}

        .lny-hero__ico{width:60px;height:60px;margin:0 auto .8rem;display:block;stroke:var(--lny-gold-bright);fill:none;stroke-width:1.7;stroke-linecap:round;stroke-linejoin:round}
        .lny-hero__orn{position:absolute;width:68px;height:auto;color:rgba(232,197,71,.4);pointer-events:none}
        .lny-hero__orn--tr{top:10px;right:10px}
        .lny-hero__orn--bl{bottom:10px;left:10px;transform:scale(-1)}
        .lny-hero__eyebrow{margin:0 0 .55rem;color:var(--lny-gold-bright);font-family:'Outfit',system-ui,sans-serif;font-size:.76rem;letter-spacing:.22em;text-transform:uppercase}
        .lny-hero__lead{margin:0;color:#fff;font-family:var(--lny-serif);font-size:1.62rem;font-weight:500;line-height:1.15;letter-spacing:.03em}
        .lny-hero__sub{margin:.4rem 0 0;color:rgba(255,246,232,.9);font-family:'Outfit',system-ui,sans-serif;font-size:1.08rem;line-height:1.45}

        .lny-hero__cta{display:flex;flex-wrap:wrap;justify-content:center;gap:.7rem;margin:1.45rem 0 0}
        .lny-hero__btn{display:inline-flex;align-items:center;justify-content:center;gap:.5rem;min-height:52px;padding:.9rem 1.7rem;border-radius:999px;
          text-decoration:none;font-family:'Outfit',system-ui,sans-serif;font-size:.78rem;font-weight:700;letter-spacing:.16em;text-transform:uppercase;border:0;cursor:pointer;
          position:relative;overflow:hidden;transition:transform .3s ease,box-shadow .3s ease}
        .lny-hero__btn--gold{background:linear-gradient(180deg,#FFF1B8 0%,#F0D56A 42%,#D4AF37 100%);color:#4A0A0C;box-shadow:0 12px 28px rgba(212,175,55,.38),inset 0 1px 0 rgba(255,255,255,.65)}
        .lny-hero__btn--gold::after{content:"";position:absolute;top:0;left:-60%;width:40%;height:100%;background:linear-gradient(100deg,transparent,rgba(255,255,255,.6),transparent);transform:skewX(-20deg) translateX(0);animation:lnyBtnSheen 5s ease-in-out infinite}
        @keyframes lnyBtnSheen{0%,70%{transform:skewX(-20deg) translateX(0)}100%{transform:skewX(-20deg) translateX(420%)}}
        .lny-hero__btn--gold:hover{transform:translateY(-2px);box-shadow:0 16px 36px rgba(212,175,55,.55),0 0 22px rgba(246,226,122,.45),inset 0 1px 0 rgba(255,255,255,.7)}
        .lny-hero__btn--sponsor{background:linear-gradient(180deg,#C41A32 0%,#9B0B1C 100%);color:#fff;border:1.5px solid rgba(255,241,184,.78);letter-spacing:.1em}
        .lny-hero__btn--sponsor:hover{transform:translateY(-2px);box-shadow:0 12px 30px rgba(0,0,0,.3),0 0 18px rgba(255,241,184,.3)}


        @media (max-width:860px){
          .lny-hero{height:auto;min-height:100svh;padding-top:clamp(6rem,18vw,7.5rem)}
          .lny-hero__title{font-size:clamp(2.2rem,12vw,3.2rem)}
          .lny-hero__celebration{letter-spacing:.28em}
          .lny-hero__cards{grid-template-columns:1fr;max-width:22rem;margin-left:auto;margin-right:auto}
          .lny-hero__cta .lny-hero__btn{width:100%}
          .lny-hero__blossoms--tl{width:42%;height:26%}
          .lny-hero__blossoms--br{width:36%;height:24%}
        }

        @media (prefers-reduced-motion:reduce){
          .lny-hero *,.lny-hero *::before,.lny-hero *::after{animation:none!important;transition:none!important}
          .lny-hero__fx{display:none}
          /* The original photo is the motion-safe fallback, including when the reel override is supplied. */
          .lny-hero__video{display:none}
          .lny-hero__plate{opacity:0!important}
          .lny-hero__plate--primary{opacity:1!important}
          /* No curtain either: left shut by a cancelled animation it would hide
             the very thing it exists to reveal. */
          .lny-hero__curtain{display:none}
        }
      `}</style>
    </section>
  );
}

/**
 * One fact card: the detail on crimson, and the same detail over the room when
 * the pointer rests on it.
 *
 * The photograph is a background, never the content, and it arrives only on
 * hover — three photographs showing by default would compete with the lockup
 * instead of rewarding a look. The facts are repeated on the back rather than
 * swapped out, so turning a card never costs the reader the thing they came for;
 * the gradient over the photo is what keeps them legible.
 *
 * Nothing is hidden behind the hover: both faces carry the same words, so a
 * visitor who never hovers, or cannot, has already read everything.
 */
function FactCard({
  icon,
  eyebrow,
  lead,
  sub,
  photo,
  photoPosition = "center",
}: {
  icon: React.ReactNode;
  eyebrow: string;
  lead: React.ReactNode;
  sub: React.ReactNode;
  photo: string;
  /** Which part of the frame this card shows, so three cards off one photograph
      do not read as the same picture printed three times. */
  photoPosition?: string;
}) {
  const facts = (
    <>
      {icon}
      <p className="lny-hero__eyebrow">{eyebrow}</p>
      <p className="lny-hero__lead">{lead}</p>
      <p className="lny-hero__sub">{sub}</p>
    </>
  );

  return (
    <article className="lny-hero__card" tabIndex={0}>
      <div className="lny-hero__card-inner">
        <div className="lny-hero__face lny-hero__face--front">
          <LotusOrnaments />
          {facts}
        </div>
        <div
          className="lny-hero__face lny-hero__face--back"
          style={{ backgroundImage: `url(${photo})`, backgroundPosition: photoPosition }}
        >
          <div className="lny-hero__face-content">{facts}</div>
        </div>
      </div>
    </article>
  );
}

/** The paired cloud flourishes in opposite corners of each card. */
function LotusOrnaments() {
  const cloud = "M6 26c8-14 22-16 30-4 6-12 20-12 26 0 8-6 20 0 18 10-2 8-12 10-22 6-6 8-18 6-24-2-8 6-22 2-28-10z";
  return (
    <>
      <svg className="lny-hero__orn lny-hero__orn--tr" viewBox="0 0 72 36" aria-hidden="true">
        <path d={cloud} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
      <svg className="lny-hero__orn lny-hero__orn--bl" viewBox="0 0 72 36" aria-hidden="true">
        <path d={cloud} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    </>
  );
}
